import { defineStore } from "pinia"
import Pusher, { Channel } from "pusher-js"
import { inject, onUnmounted, ref } from "vue"
import { TASK_STATE } from "@core/constants"
import type { Services } from "@services"
import type { Task } from "@types"
import { useToast } from "primevue/usetoast"
import { createUniqueId } from "@core/helpers"

/** Limite de messages mis en attente par tâche (évite une croissance infinie si followEvent n’est jamais appelé). */
const MAX_PENDING_TASK_RAW = 50

/**
 * Options de suivi Pusher (même contrat que `followEvent` sur le store tâches).
 */
export type FollowEventTaskOptions = {
    taskname?: string
    taskToastTitle?: string
    taskToastFailureMessage?: string
    taskToastSuccessMessage?: string
    /** Masque le sous-texte TaskToast (étapes, libellé de succès) pour cette tâche. */
    taskToastHideSubtitle?: boolean
    notification?: boolean
    autoDelete?: boolean
    /** Préremplit le décompte si l’init Pusher a été manqué (course avec le bind). */
    subtasks_total?: number
    onReceived?: () => void
    onSuccess?: (data: any) => void
    onError?: (error: any) => void
}

/**
 * Options de `launchTask` : suivi Pusher + pré-bind canal.
 */
export type LaunchTaskOptions = FollowEventTaskOptions & {
    /** Par défaut `true`. `false` si aucune inscription store après création (pas de suivi toast / Pusher côté store). */
    preBindTaskChannel?: boolean
}

/**
 * Options de `followTask` : même socle que `followEvent`, plus l’état initial (canal utilisateur, pas d’`event_name`).
 */
export type FollowTaskOptions = FollowEventTaskOptions & {
    state?: string
}

export const useTaskStore = defineStore("task", () => {
    const toast = useToast()

    const tasks = ref<Task[]>([])

    const pusher = inject<Pusher>("pusher")

    const services = inject<Services>("services")

    const channelTask = ref<Channel | null>(null)

    /** Noms d'événements déjà bindés sur le canal `task` (évite les handlers en double). */
    const boundTaskEventNames = new Set<string>()

    /**
     * Payloads Pusher reçus avant que la tâche soit dans le store (course réseau / worker).
     * Clé = identifiant de tâche parent côté store (`task_id` API).
     */
    const pendingRawsByTaskId = new Map<string, any[]>()

    onUnmounted(() => {
        if (pusher) {
            pusher.unsubscribe("notifications")
        }
    })

    // ==============================================
    // Fonction pour la gestion des tâches
    // ==============================================

    /**
     * Branche un utilisateur à un canal pusher dédié à ses tâches
     * en fonction de son identifiant
     * @param userId - Identifiant de l'utilisateur
     */
    function signInPusher(userId: string) {
        if (pusher) {
            const channel = pusher.subscribe(`tasks-user-${userId}`)
            channel.bind_global(bindUserChannel)
            channelTask.value = pusher.subscribe("task")
            boundTaskEventNames.clear()
            pendingRawsByTaskId.clear()
        }
    }
    /**
     * Permet de suivre une tâche via le canal utilisateur Pusher (`bindUserChannel`), sans `event_name` sur le canal `task`.
     * @param task_id - Identifiant de la tâche
     * @param options - Même champs que `FollowEventTaskOptions` + `state` initial éventuel
     */
    function followTask(task_id: string, options: FollowTaskOptions = {}) {
        const state = options.state ?? TASK_STATE.EMPTY
        const taskname = options.taskname ?? ""
        const notification = options.notification ?? true
        const autoDelete = options.autoDelete ?? false
        const subtasksTotalOption = options.subtasks_total

        tasks.value.push({
            task_id,
            display: false,
            state,
            taskname,
            taskToastTitle: options.taskToastTitle,
            taskToastFailureMessage: options.taskToastFailureMessage,
            taskToastSuccessMessage: options.taskToastSuccessMessage,
            taskToastHideSubtitle: options.taskToastHideSubtitle,
            notification,
            progress_tasks: [],
            autoDelete,
            ...(typeof subtasksTotalOption === "number" &&
            subtasksTotalOption > 0
                ? { subtasks_total: subtasksTotalOption }
                : {}),
            onReceived: options.onReceived ?? (() => {}),
            onSuccess: options.onSuccess ?? (() => {}),
            onError: options.onError ?? (() => {}),
        })
    }

    /**
     * Enregistre la tâche dans le store et raccorde le canal Pusher (`followEvent` / `launchTask`).
     */
    function registerFollowedTask(
        task_id: string,
        event_name: string,
        options: FollowEventTaskOptions = {}
    ) {
        if (!task_id || !event_name) {
            console.warn("Cannot follow task: missing task_id or event_name")
            return
        }

        if (!channelTask.value) return

        const taskname = options.taskname ?? ""
        const notification = options.notification ?? true
        const autoDelete = options.autoDelete ?? false
        const subtasksTotalOption = options.subtasks_total

        tasks.value.push({
            task_id,
            display: false,
            state: TASK_STATE.EMPTY,
            taskname,
            taskToastTitle: options.taskToastTitle,
            taskToastFailureMessage: options.taskToastFailureMessage,
            taskToastSuccessMessage: options.taskToastSuccessMessage,
            taskToastHideSubtitle: options.taskToastHideSubtitle,
            notification,
            event_name,
            progress_tasks: [],
            autoDelete,
            ...(typeof subtasksTotalOption === "number" &&
            subtasksTotalOption > 0
                ? { subtasks_total: subtasksTotalOption }
                : {}),
            onReceived: options.onReceived ?? (() => {}),
            onSuccess: options.onSuccess ?? (() => {}),
            onError: options.onError ?? (() => {}),
        })

        ensureTaskChannelListener(event_name)
        flushPendingForTask(task_id)

        /** Même comportement que {@link launchTask} : toast de suivi dès l’enregistrement, pas seulement au 1er message Pusher. */
        if (notification) {
            createNotifcation(task_id)
        }
    }

    /**
     * Permet de suivre une tâche via son event pusher
     * @param task_id - Identifiant de la tâche
     * @param event_name - Nom de l'évènement pusher
     * @param options.taskToastTitle - Titre personnalisé affiché dans le toast (prioritaire sur la clé i18n)
     * @param options.taskToastFailureMessage - Message personnalisé en cas d'échec (prioritaire sur les libellés i18n d’échec TaskToast)
     * @param options.taskToastSuccessMessage - Message personnalisé en cas de succès (prioritaire sur la clé i18n success_task)
     * @param options.taskToastHideSubtitle - Masque étapes et message de succès dans TaskToast
     * @param options.subtasks_total - Nombre de sous-tâches attendu si l’API le fournit (sinon Celery envoie souvent `step: init` en Pusher, parfois avant le bind client).
     */
    function followEvent(
        task_id: string,
        event_name: string,
        options: FollowEventTaskOptions = {}
    ) {
        registerFollowedTask(task_id, event_name, options)
    }

    /**
     * Lance une tâche côté et renvoie son task_id pour le suivi
     * @param taskname - Nom de la tâche
     * @param payload - Payload de la tâche
     * @param event_name - Nom d’événement Pusher (sinon identifiant généré côté client)
     * @param options - Avec `preBindTaskChannel` à `true` (défaut), enregistre l’écoute Pusher avant l’API puis enregistre la tâche comme `followEvent` avec les mêmes champs optionnels (notification, toasts, callbacks…), et affiche la notif toast dès que possible.
     * @returns Identifiant de la tâche et nom de l'évènement pusher associé
     */
    async function launchTask(
        taskname: string,
        payload: unknown,
        event_name?: string,
        options?: LaunchTaskOptions
    ) {
        if (!services?.$createTask) {
            throw new Error("Service de création de tâche non disponible")
        }
        const id = createUniqueId()

        const eventName = event_name ? event_name : id

        if (!eventName) {
            throw new Error("Impossible de générer un nom d'évènement unique")
        }

        const preBindTaskChannel = options?.preBindTaskChannel ?? true
        if (preBindTaskChannel) {
            ensureTaskChannelListener(eventName)
        }

        const response = await services.$createTask({
            name: taskname,
            kwargs: payload as Record<string, unknown>,
            queue: "front",
            request: {
                event_name: eventName,
                tracker: {
                    sent_at: new Date(),
                    sent_by: "front",
                },
                subscribe: true,
            },
        })

        if (preBindTaskChannel && channelTask.value) {
            const followOpts: FollowEventTaskOptions = {
                taskname: options?.taskname ?? taskname,
                taskToastTitle: options?.taskToastTitle,
                taskToastFailureMessage: options?.taskToastFailureMessage,
                taskToastSuccessMessage: options?.taskToastSuccessMessage,
                taskToastHideSubtitle: options?.taskToastHideSubtitle,
                notification: options?.notification ?? true,
                autoDelete: options?.autoDelete ?? false,
                subtasks_total: options?.subtasks_total,
                onReceived: options?.onReceived,
                onSuccess: options?.onSuccess,
                onError: options?.onError,
            }
            registerFollowedTask(response.task_id, eventName, followOpts)
            if (followOpts.notification) {
                createNotifcation(response.task_id)
            }
        }

        return {
            task_id: response.task_id,
            event_name: eventName,
        }
    }

    /**
     * Bind les évènements pusher reçu au gestionnaire de tâche `updateTaskByState`
     * @param eventName - Nom de l'évènement
     * @param data - Contenue de l'évènement
     * @returns
     */
    async function bindUserChannel(eventName: string, data: any) {
        const taskId = data.task_id

        if (!taskId) return

        // Trouve la tâche dans le store
        const taskIndex = tasks.value.findIndex(
            (task) => task.task_id === taskId
        )

        if (taskIndex === -1) {
            console.warn("Received event for unknown task:", taskId)
            return
        }

        // Si la tâche doit afficher une notification, on l'affiche
        if (tasks.value[taskIndex]?.notification) {
            // Affiche la notification si elle n'est pas déjà affichée
            createNotifcation(taskId)
        }

        // si la tâche n'existe pas encore dans le store, on attend le nextTick pour la trouver
        if (eventName === TASK_STATE.RECEIVED && taskIndex === -1) {
            setTimeout(() => {
                updateTaskByState(taskId, eventName, data)
            }, 200)
        } else {
            updateTaskByState(taskId, eventName, data)
        }
    }

    /**
     * Unbind le channel task pour un eventName donné
     * @param eventName - Nom de l'évènement à unbind
     */
    function unbindTaskChannel(eventName: string) {
        if (channelTask.value) {
            channelTask.value.unbind(eventName)
        }
        boundTaskEventNames.delete(eventName)
        const task = tasks.value.find((t) => t.event_name === eventName)
        if (task) {
            pendingRawsByTaskId.delete(task.task_id)
        }
    }

    /**
     * Identifiant de tâche « parent » attendu dans le store (même clé que `task.task_id`).
     */
    function getStoreTaskIdFromPusherRaw(raw: any): string | null {
        if (typeof raw !== "object" || raw === null) return null
        const isNewFormat =
            "step" in raw || "status" in raw || "task_id_parent" in raw
        if (isNewFormat) {
            const id = raw.task_id_parent || raw.task_id
            return typeof id === "string" ? id : null
        }
        const id = raw.task_id
        return typeof id === "string" ? id : null
    }

    /**
     * Traite un message du canal `task` pour un nom d'événement donné.
     */
    function processTaskChannelMessage(eventName: string, raw: any) {
        // --------------------------------------------------
        // Nouveau format (messages avec step / status / task_id_parent)
        // --------------------------------------------------
        const isNewFormat =
            typeof raw === "object" &&
            ("step" in raw || "status" in raw || "task_id_parent" in raw)

        if (isNewFormat) {
            try {
                const parentId = raw.task_id_parent || raw.task_id
                if (!parentId) return

                const isParentTask = !raw.task_id_parent

                // Mapping de l'eventName attendu par updateTaskByState
                let mappedEvent: string = TASK_STATE.PROGRESS

                // Cas init (premier message parent) — l’API envoie souvent `step: "init"` plutôt que `status`
                if (
                    (raw.status === "init" || raw.step === "init") &&
                    isParentTask
                ) {
                    mappedEvent = TASK_STATE.RECEIVED
                }
                // Etapes de sous-tâches
                else if (raw.step === "prerun" && !isParentTask) {
                    mappedEvent = TASK_STATE.PROGRESS
                } else if (raw.step === "postrun" && !isParentTask) {
                    mappedEvent = TASK_STATE.PROGRESS // on reste en PROGRESS pour mettre à jour la sous-tâche
                }
                // Fin de la tâche parent
                else if (raw.step === "postrun" && isParentTask) {
                    // `status` SUCCESS + `flag` FAILURE = exécution OK, message métier informatif (pas un échec de tâche).
                    const informationalOutcome =
                        raw.status === "SUCCESS" &&
                        (raw.flag === "FAILURE" ||
                            raw.result?.flag === "FAILURE")
                    if (informationalOutcome) {
                        mappedEvent = TASK_STATE.SUCCESS
                    } else {
                        const businessFailure =
                            raw.flag === "FAILURE" ||
                            raw.result?.flag === "FAILURE"
                        const transportOk =
                            raw.status === "SUCCESS" || raw.flag === "SUCCESS"
                        const success =
                            !businessFailure &&
                            transportOk &&
                            raw.status !== "FAILURE"
                        mappedEvent = success
                            ? TASK_STATE.SUCCESS
                            : TASK_STATE.FAILURE
                    }
                }

                // Construction du messageContent attendu par updateTaskByState
                const messageContent: any = {
                    // On ne met taskname que pour les événements parent, pas pour les sous-tâches
                    taskname: isParentTask ? raw.name : undefined,
                    data: {},
                }

                if (isParentTask) {
                    // SUCCESS / FAILURE parent -> on injecte un status_code pour la logique existante
                    if (
                        mappedEvent === TASK_STATE.SUCCESS ||
                        mappedEvent === TASK_STATE.FAILURE
                    ) {
                        messageContent.data = {
                            status_code:
                                mappedEvent === TASK_STATE.SUCCESS ? 200 : 500,
                            flag: raw.flag,
                            message: raw.message,
                            result: raw.result,
                        }
                    } else if (mappedEvent === TASK_STATE.RECEIVED) {
                        // Message init: on ne met que des méta infos
                        messageContent.data = {
                            status_code: 100,
                            subtasks: raw.result?.subtasks,
                        }
                    }
                } else {
                    // Sous-tâche -> progress_task
                    const subTaskState =
                        raw.step === "postrun"
                            ? raw.flag === "SUCCESS"
                                ? TASK_STATE.SUCCESS
                                : TASK_STATE.FAILURE
                            : TASK_STATE.PROGRESS
                    messageContent.data = {
                        sub_task_id: raw.task_id,
                        sub_taskname: raw.name,
                        label: raw.description || raw.name,
                        state: subTaskState,
                        message: raw.message || raw.description,
                        nsteps: undefined,
                        step: undefined,
                    }
                }

                // Parent : phase informative (prerun) — ne pas alimenter progress_tasks ni écraser des clés vides
                if (
                    isParentTask &&
                    mappedEvent === TASK_STATE.PROGRESS &&
                    raw.step === "prerun"
                ) {
                    const tIdx = tasks.value.findIndex(
                        (t) => t.task_id === parentId
                    )
                    const existing = tIdx !== -1 ? tasks.value[tIdx] : undefined
                    updateTask(parentId, {
                        state: TASK_STATE.PROGRESS,
                        taskname: raw.name ?? existing?.taskname,
                        current_step_message:
                            raw.description ?? raw.message ?? "",
                    })
                    return
                }

                // On applique la mise à jour sur la tâche parent (une seule entrée dans le store)
                updateTaskByState(parentId, mappedEvent, messageContent)
            } catch (err) {
                console.warn("Remap pusher task event failed", err)
            }
            return
        }

        // --------------------------------------------------
        // Ancien comportement (format déjà compatible)
        // --------------------------------------------------
        const taskId = raw.task_id
        if (!taskId) return
        updateTaskByState(taskId, eventName, raw)
    }

    /**
     * Rejoue les messages reçus avant l’enregistrement de la tâche dans le store.
     */
    function flushPendingForTask(taskId: string) {
        const pending = pendingRawsByTaskId.get(taskId)
        if (!pending?.length) return
        pendingRawsByTaskId.delete(taskId)
        const task = tasks.value.find((t) => t.task_id === taskId)
        const eventName = task?.event_name
        if (!eventName) return
        for (const raw of pending) {
            processTaskChannelMessage(eventName, raw)
        }
    }

    /**
     * Lie une seule fois le canal `task` au nom d'événement (avant l’API si besoin).
     */
    function ensureTaskChannelListener(eventName: string) {
        if (!channelTask.value || boundTaskEventNames.has(eventName)) return
        boundTaskEventNames.add(eventName)
        channelTask.value.bind(eventName, (raw: any) => {
            const storeTaskId = getStoreTaskIdFromPusherRaw(raw)
            if (!storeTaskId) return
            if (
                tasks.value.findIndex((t) => t.task_id === storeTaskId) === -1
            ) {
                const list = pendingRawsByTaskId.get(storeTaskId) ?? []
                list.push(raw)
                while (list.length > MAX_PENDING_TASK_RAW) {
                    list.shift()
                }
                pendingRawsByTaskId.set(storeTaskId, list)
                return
            }
            processTaskChannelMessage(eventName, raw)
        })
    }

    /**
     * Gestion centraliser des événements pusher
     * @param taskId - Identifiant de la tâche
     * @param eventName - Nom de l'évènement
     * @param messageContent - Contenue du message de l'évènement
     * @returns
     */
    function updateTaskByState(
        taskId: string,
        eventName: string,
        messageContent: any
    ) {
        // Récupère l'index de la tâche dans le store
        const taskIndex = tasks.value.findIndex(
            (task) => task.task_id === taskId
        )

        if (taskIndex === -1) return

        // Référence à la tâche
        const task = tasks.value[taskIndex]

        // Nom de la tâche
        const taskname = task.taskname ? task.taskname : messageContent.taskname

        // Contenu du message (format imbriqué `.data` ou payload plat legacy)
        const taskContent = messageContent?.data ?? messageContent

        // En fonction du type d'évènement, on met à jour la tâche
        switch (eventName) {
            // tâche vient d'être reçue par Celery
            case TASK_STATE.RECEIVED: {
                const rawSubtasks = taskContent?.subtasks
                const subtasks_total =
                    typeof rawSubtasks === "number"
                        ? rawSubtasks
                        : Array.isArray(rawSubtasks)
                          ? rawSubtasks.length
                          : undefined
                // Init Pusher parfois reçu après des sous-tâches : ne pas repasser en RECEIVED
                const nextState =
                    task.state === TASK_STATE.PROGRESS ||
                    task.state === TASK_STATE.STARTED ||
                    task.state === TASK_STATE.SUCCESS
                        ? task.state
                        : TASK_STATE.RECEIVED
                updateTask(taskId, {
                    state: nextState,
                    taskname,
                    ...(subtasks_total !== undefined ? { subtasks_total } : {}),
                })
                // Appel du callback si défini
                if (tasks.value[taskIndex]?.onReceived) {
                    tasks.value[taskIndex].onReceived?.()
                }
                break
            }
            // tâche vient d'être démarrée par Celery
            case TASK_STATE.STARTED:
                // mise à jour de la tâche
                updateTask(taskId, {
                    state: TASK_STATE.STARTED,
                    taskname,
                })
                // Appel du callback si défini
                if (tasks.value[taskIndex]?.onReceived) {
                    tasks.value[taskIndex].onReceived?.()
                }

                break
            // tâche est en cours de traitement
            case TASK_STATE.PROGRESS: {
                // Appel du callback si défini
                if (tasks.value[taskIndex]?.onReceived) {
                    tasks.value[taskIndex].onReceived?.()
                }
                const hasChildProgress =
                    taskContent &&
                    (taskContent.sub_task_id != null ||
                        (taskContent.sub_taskname != null &&
                            taskContent.sub_taskname !== ""))
                if (hasChildProgress) {
                    updateProgressTask(taskId, messageContent)
                }

                // mise à jour du statut de la tâche
                updateTask(taskId, {
                    state: TASK_STATE.PROGRESS,
                    taskname,
                })
                break
            }
            // tâche terminée avec succès
            case TASK_STATE.SUCCESS: {
                // Si la tâche renvoie une erreur dans le détail de la tâche, on met à jour l'état de la tâche à échec

                // L’échec ne se déduit pas de `flag` seul : status SUCCESS + flag FAILURE est traité
                // comme information (voir `processTaskChannelMessage`), avec `status_code` 200.
                const state =
                    taskContent?.status_code >= 400
                        ? TASK_STATE.FAILURE
                        : TASK_STATE.SUCCESS

                const resultSub = taskContent?.result?.subtasks
                const subtasksFromSuccess =
                    task.subtasks_total == null &&
                    typeof resultSub === "number" &&
                    resultSub > 0
                        ? resultSub
                        : undefined

                // Mise à jour du statut de la tâche et de la réponse
                updateTask(taskId, {
                    state,
                    taskname,
                    return: taskContent,
                    ...(subtasksFromSuccess !== undefined
                        ? { subtasks_total: subtasksFromSuccess }
                        : {}),
                })

                // Si la tâche est en échec, on appelle le callback onError
                if (state === TASK_STATE.FAILURE) {
                    if (task?.onError) {
                        task.onError?.({
                            data: taskContent,
                            task_id: taskId,
                        })
                    }
                    const tAfter = tasks.value[taskIndex] ?? task
                    if (tAfter?.event_name) {
                        unbindTaskChannel(tAfter.event_name)
                    }
                    if (tAfter?.notification) {
                        createNotifcation(taskId)
                    }
                    return
                }

                // URL directe : legacy (`data.file_url`), worker Celery (`result.url` / `result.url_excel`), ou racine (`file_url`)
                const successDownloadUrl = [
                    taskContent?.data?.file_url,
                    taskContent?.result?.url,
                    taskContent?.result?.url_excel,
                    taskContent?.file_url,
                ].find((u) => typeof u === "string" && u.length > 0)

                if (successDownloadUrl) {
                    window.open(successDownloadUrl, "_blank")
                    if (task.autoDelete) {
                        removeTaskNotificationAndTask(taskId)
                    }
                    return
                }

                // Si on a un data_url, on ouvre le fichier JSON et on récupère les données qu'on envoie dans le callback de la fonction
                if (taskContent.data_url) {
                    // Mise à jour des sous tâches
                    updateProgressTask(taskId, {
                        data: {
                            sub_taskname: "fetch_result_file",
                            state: TASK_STATE.PROGRESS,
                            message: "Récupérations des résultats",
                        },
                    })

                    fetch(taskContent.data_url)
                        .then((response) => response.json())
                        .then((data) => {
                            if (tasks.value[taskIndex]?.onSuccess) {
                                tasks.value[taskIndex].onSuccess?.({
                                    data,
                                    task_id: taskId,
                                })
                            }
                        })
                        .finally(() => {
                            // Mise à jour de la sous tâche de récupération des résultats
                            updateProgressTask(taskId, {
                                data: {
                                    sub_taskname: "fetch_result_file",
                                    state: TASK_STATE.SUCCESS,
                                    message: "Récupérations des résultats",
                                },
                            })
                            if (task.autoDelete) {
                                removeTaskNotificationAndTask(taskId)
                            }
                        })
                }
                // Si pas d'URL spéciale et autoDelete demandé : callback + suppression toast
                // Sauf message métier informatif (status OK + flag FAILURE) : on garde le toast visible, comme un FAILURE
                const isInformationalBusinessOutcome =
                    (taskContent?.flag === "FAILURE" ||
                        taskContent?.result?.flag === "FAILURE") &&
                    taskContent?.status_code === 200
                if (!taskContent.data_url && task.autoDelete) {
                    if (tasks.value[taskIndex]?.onSuccess) {
                        tasks.value[taskIndex].onSuccess?.({
                            task_id: taskId,
                            data: taskContent,
                        })
                    }
                    if (!isInformationalBusinessOutcome) {
                        removeTaskNotificationAndTask(taskId)
                    }
                }
                // Unbind le channel si la tâche a un event_name
                if (task.event_name) {
                    unbindTaskChannel(task.event_name)
                }
                break
            }
            // tâche terminée avec erreur
            case TASK_STATE.FAILURE:
                // mise à jour de la tâche (payload plat ou `.data` selon le format)
                updateTask(taskId, {
                    state: TASK_STATE.FAILURE,
                    taskname,
                    return: taskContent?.data ?? taskContent,
                })
                if (task?.onError) {
                    task.onError?.({
                        data: taskContent,
                        task_id: taskId,
                    })
                }
                // Unbind le channel si la tâche a un event_name
                if (task.event_name) {
                    unbindTaskChannel(task.event_name)
                }
                break
        }
        // Si la tâche doit afficher une notification, on l'affiche
        if (tasks.value[taskIndex].notification) {
            // Affiche la notification si elle n'est pas déjà affichée
            createNotifcation(taskId)
        }
    }

    // ==============================================
    // Fonction utilitaire pour la gestion des tâches
    // ==============================================

    /**
     * Permet de mettre à jour une tâche
     * @param task_id
     * @param update
     */
    function updateTask(task_id: string, update: any) {
        const taskIndex = tasks.value.findIndex(
            (task) => task.task_id === task_id
        )
        if (taskIndex !== -1) {
            tasks.value.splice(
                taskIndex,
                1,
                Object.assign({}, tasks.value[taskIndex], update)
            )
        }
    }

    /**
     * Met à jour le tableau `progress_tasks` d'une tâche.
     * Avec `sub_task_id` (enfant Celery) : clé stable pour ne pas écraser des sous-tâches au même `name`.
     * Sans `sub_task_id` : comportement historique sur `sub_taskname` seul.
     */
    function updateProgressTask(task_id: string, messageContent: any) {
        const taskContent = messageContent?.data ?? messageContent
        if (!taskContent) return

        const taskIndex = tasks.value.findIndex(
            (task) => task.task_id === task_id
        )
        if (taskIndex === -1) return

        const task = tasks.value[taskIndex]
        const subId = taskContent.sub_task_id
        const subName = taskContent.sub_taskname

        const progressTaskIndex = task.progress_tasks.findIndex((pt) => {
            if (subId != null && String(subId).length > 0) {
                return pt.sub_task_id === subId
            }
            if (subName != null) {
                return pt.sub_taskname === subName
            }
            return false
        })

        const progress_tasks = [...task.progress_tasks]

        const patch = {
            sub_task_id:
                taskContent.sub_task_id ??
                progress_tasks[progressTaskIndex]?.sub_task_id,
            sub_taskname:
                taskContent.sub_taskname ??
                progress_tasks[progressTaskIndex]?.sub_taskname ??
                "",
            label:
                taskContent.label ??
                progress_tasks[progressTaskIndex]?.label ??
                taskContent.sub_taskname,
            state: taskContent.state,
            message: taskContent.message,
            nsteps: taskContent.nsteps,
            step: taskContent.step,
        }

        if (progressTaskIndex !== -1) {
            progress_tasks.splice(
                progressTaskIndex,
                1,
                Object.assign({}, progress_tasks[progressTaskIndex], patch)
            )
        } else {
            progress_tasks.push(patch as Task["progress_tasks"][number])
        }

        updateTask(task_id, {
            progress_tasks,
        })
    }

    /**
     * Permet de créer une notification pour une tâche donnée
     * @param task_id - Identifiant de la tâche
     * @returns
     */
    function createNotifcation(task_id: string) {
        const taskIndex = tasks.value.findIndex(
            (task) => task.task_id === task_id
        )
        if (taskIndex !== -1) {
            if (tasks.value[taskIndex].display) return

            tasks.value.splice(
                taskIndex,
                1,
                Object.assign({}, tasks.value[taskIndex], {
                    display: true,
                })
            )
            const notificationInstance = {
                severity: "info",
                summary: "Task",
                detail: task_id,
                group: "tasks",
            }
            toast.add(notificationInstance)
            // Stocke l'instance de notification dans la tâche
            updateTask(task_id, {
                notificationInstance,
            })
        }
    }

    /**
     * Supprime la notification d'une tâche (sans toucher aux autres) et retire la tâche si présente
     * @param task_id - Identifiant de la tâche
     */
    function removeTaskNotificationAndTask(task_id: string) {
        const task = tasks.value.find((t) => t.task_id === task_id)
        if (task?.notificationInstance) {
            toast.remove(task.notificationInstance)
        }
        // Retire la tâche du store
        removeTask(task_id)
    }

    /**
     * Réccupère une tâche par son identifiant
     * @param task_id - Identifiant de la tâche
     * @returns
     */
    function getTaskById(task_id: string) {
        return tasks.value.find((task) => task.task_id === task_id)
    }

    /**
     * Récupère une tâche par son nom
     * @param taskname - Nom de la tâche
     */
    function getTaskByTaskname(taskname: string) {
        return tasks.value.find((task) => task.taskname === taskname)
    }

    /**
     * Permet de supprimer une tâche du store par son identifiant
     * @param task_id - Identifiant de la tâche
     */
    function removeTask(task_id: string) {
        const taskIndex = tasks.value.findIndex(
            (task) => task.task_id === task_id
        )
        if (taskIndex !== -1) {
            tasks.value.splice(taskIndex, 1)
        }
    }

    /**
     * Réinitialise le store à son état initial (vide la liste des tâches).
     */
    function $reset() {
        tasks.value = []
        channelTask.value = null
        boundTaskEventNames.clear()
        pendingRawsByTaskId.clear()
    }

    return {
        tasks,
        launchTask,
        followEvent,
        followTask,
        signInPusher,
        getTaskById,
        getTaskByTaskname,
        updateTask,
        createNotifcation,
        removeTask,

        // Reset
        $reset,
    }
})
